import { apiFetch } from "@/lib/api";

export const WIRELESS_CHECK_DEVICE_QUERY_KEY = ["manufacturing", "wireless-check", "device"] as const;

export interface WirelessCheckPortalSummary {
  hasWirelessCheckin: boolean;
  checkinCount: number;
  hasValidGps: boolean;
  latestCheckinAt: string | null;
  latestValidGpsAt: string | null;
  latestValidGpsLat: number | null;
  latestValidGpsLong: number | null;
}

export interface WirelessCheckEpisode {
  attemptCount: number;
  passed: boolean;
  passSource: "portal" | "override" | null;
  passedAt: string | null;
  qcIncidentId: number | null;
  lastCheckStartedAt: string | null;
}

export interface WirelessCheckDevice {
  serialFciId: number;
  deviceLabelSerial: string;
  finishedSku: string | null;
  imei: string | null;
  configuredAt: string | null;
  stationId: number | null;
  stationName: string | null;
  requiresWirelessCheck: boolean;
  portal: WirelessCheckPortalSummary;
  portalLookupError: string | null;
  episode: WirelessCheckEpisode;
}

export interface WirelessCheckAttempt {
  recorded: boolean;
  attemptNumber: number | null;
  outcome: "pass" | "fail" | null;
  episodePassed: boolean;
  passSource: "portal" | "override" | null;
  requireQcIncident: boolean;
  operatorMessage: string | null;
  cooldownBlocked: boolean;
  lastCheckStartedAt: string | null;
  nextCheckAllowedAt: string | null;
  remainingMs: number;
  suggestedFailureKind: "check_in_failing" | "no_coordinates" | "bad_coordinates" | null;
  suggestedFailureModeId: number | null;
  suggestedFailureModeName: string | null;
}

export interface WirelessCheckScanResponse {
  device: WirelessCheckDevice;
  laborRecorded: boolean;
  stationAdvanced: boolean;
  testStationAdvanced: boolean;
  attempt: WirelessCheckAttempt;
}

export interface WirelessCheckLoginUser {
  id: string;
  email: string;
  displayName: string | null;
  hasPin: boolean;
}

export function buildWirelessCheckScanBody(serial: string): { serial: string } {
  return { serial };
}

export function fetchWirelessCheckDevice(serial: string): Promise<{ device: WirelessCheckDevice }> {
  const params = new URLSearchParams({ serial });
  return apiFetch<{ device: WirelessCheckDevice }>(
    `/api/manufacturing/wireless-check/device?${params.toString()}`,
  );
}

export function scanWirelessCheckDevice(serial: string): Promise<WirelessCheckScanResponse> {
  return apiFetch<WirelessCheckScanResponse>("/api/manufacturing/wireless-check/scan", {
    method: "POST",
    body: buildWirelessCheckScanBody(serial),
  });
}

export function fetchWirelessCheckLoginUsers(): Promise<{ users: WirelessCheckLoginUser[] }> {
  return apiFetch<{ users: WirelessCheckLoginUser[] }>("/api/manufacturing/wireless-check/login-users");
}

export function overrideWirelessCheck(input: {
  serial: string;
  mdmUserId: string;
  pin: string;
}): Promise<{
  ok: true;
  overrideUserDisplayName: string | null;
  device: WirelessCheckDevice;
  testStationAdvanced: boolean;
}> {
  return apiFetch("/api/manufacturing/wireless-check/override", {
    method: "POST",
    body: input,
  });
}

export function linkWirelessCheckQcIncident(serial: string, incidentId: number): Promise<{ ok: true }> {
  return apiFetch("/api/manufacturing/wireless-check/link-qc-incident", {
    method: "POST",
    body: { serial, incidentId },
  });
}
